import { useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { Link, useHistory } from 'react-router-dom'
import clsx from 'clsx'

import { makeStyles } from '@material-ui/core/styles'
import { Box, FormHelperText } from '@material-ui/core'

import { HomeTab } from './HomeTabTitle'
import { AccountNavigation } from './AccountNavigation'
import { AddStoreAccountButton, AddStoreAccountStub } from './AddStoreAccountButton'

import { useStore } from '../hooks/storeHook'
import { useIntegrationRoute } from '../hooks/routeHooks'

import { appRoutes } from '../common/appRoutes'
import { required } from '../common/objectUtils'
import { exact, UnprocessedValueError } from '../common/tsUtils'
import { makeOzonAccountName, makeWbAccountName, makeYandexMarketAccountName } from '../common/accountUtils'

import { StoreAccountModel } from '../types/accountProps'
import { integrationKeyByType, StoreIntegrationType } from '../types/integrationTypeUtils'

import { IntegrationType } from '../server/mpsklad_core/Entity/IntegrationType'

export const SideBarNavigation =
  observer(() => {
    const {integration, accountId} = useIntegrationRoute()

    const {
      syncStore: {moySkladAccount, ozonAccounts, wbAccounts, yandexMarketAccounts},
      allowedIntegrations,
      hasMoySkladApp
    } = useStore()

    const history = useHistory()

    const classes = useStyles()

    const storeTabs = useMemo(() => {
      const result: HomeTab[] = []

      if (allowedIntegrations[IntegrationType.Ozon] &&
          (!integration ||
           integration === IntegrationType.Ozon ||
           integration === IntegrationType.MoySklad)) {
        result.push(
          ...ozonAccounts.map(ozonAccount => exact<HomeTab>({
            ozonAccount,
            id: ozonAccount.id,
            title: makeOzonAccountName(ozonAccount),
            integrationType: IntegrationType.Ozon
          })))
      }

      if (allowedIntegrations[IntegrationType.Wildberries]
          && (!integration ||
              integration === IntegrationType.Wildberries ||
              integration === IntegrationType.MoySklad)) {
        result.push(
          ...wbAccounts.map(wbAccount => exact<HomeTab>({
            wbAccount,
            id: wbAccount.id,
            title: makeWbAccountName(wbAccount),
            integrationType: IntegrationType.Wildberries
          })))
      }

      if (allowedIntegrations[IntegrationType.YandexMarket]
          && (!integration ||
              integration === IntegrationType.YandexMarket ||
              integration === IntegrationType.MoySklad)) {
        result.push(
          ...yandexMarketAccounts.map(yandexMarketAccount => exact<HomeTab>({
            yandexMarketAccount,
            id: yandexMarketAccount.id,
            title: makeYandexMarketAccountName(yandexMarketAccount),
            integrationType: IntegrationType.YandexMarket
          })))
      }

      return result
    }, [integration, allowedIntegrations, ozonAccounts, wbAccounts, yandexMarketAccounts])

    let shouldBeAllUnselected: boolean
    let storeIntegration: StoreIntegrationType | null

    switch (integration) {
      case undefined:
      case IntegrationType.MoySklad:
        shouldBeAllUnselected = true
        storeIntegration = null
        break

      case IntegrationType.Ozon:
      case IntegrationType.Wildberries:
      case IntegrationType.YandexMarket:
        shouldBeAllUnselected = false
        storeIntegration = integration
        break

      default:
        throw new UnprocessedValueError('IntegrationType', integration)
    }

    if (moySkladAccount == null) {
      return <Box className={clsx(classes.hint, 'default-border')}>
        <FormHelperText className={classes.helperText}>
          Для работы с товарами добавьте&nbsp;
          <Link to={appRoutes.MoySklad.settings} className={classes.addLink}>
            аккаунт МоегоСклада
          </Link>.
        </FormHelperText>
      </Box>
    }

    if (ozonAccounts.length === 0 && wbAccounts.length === 0 && yandexMarketAccounts.length === 0) {
      return (
        <Box padding={3} minWidth={200}>
          {
            storeIntegration
            ? <AddStoreAccountButton withHelpText integration={storeIntegration}/>
            : <AddStoreAccountStub/>
          }
        </Box>
      )
    }

    const onTabChange =
      (newAccount: StoreAccountModel) => {
        const redirectUrl = appRoutes.Store.products.makeUrl({
          accountId: newAccount.id,
          integration: integrationKeyByType[newAccount.integrationType]
        })

        history.push(redirectUrl)
      }

    return (
      <div className="sidebar_container">
        <Box className="sticky_sidebar">
          {
            storeTabs.map(
              tab =>
                <AccountNavigation
                  key={`${tab.integrationType}_${tab.id}`}
                  isSelected={tab.id === accountId && !shouldBeAllUnselected}
                  title={tab.title}
                  account={required(tab.ozonAccount ?? tab.wbAccount ?? tab.yandexMarketAccount)}
                  hasMoySkladApp={hasMoySkladApp}
                  onAccountChange={onTabChange}
                />)
          }

          {
            !!storeIntegration && !shouldBeAllUnselected &&
            <AddStoreAccountButton integration={storeIntegration}/>
          }
        </Box>
      </div>
    )
  })

const useStyles = makeStyles(
  () => ({
    addLink: {
      textDecoration: 'none'
    },
    hint: {
      margin: 20,
      padding: 20,
      maxWidth: 160,
      maxHeight: 60,
      backgroundColor: '#FBFBFD'
    },
    helperText: {
      fontSize: 11,
      fontWeight: 400,
      marginTop: 5
    }
  }))